import { useLoadScript } from '@react-google-maps/api'
import { Spin } from 'antd'
import qs from 'qs'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SuccessfullImage from '../../assets/images/new-withdrawal.png'
import { useLanguageContext } from '../../contexts/LanguageContext'
import { RootState } from '../../redux'
import { getOnboardingData, IData, postOnboardingData } from '../../redux/onboarding/onboardingActions'
import WelcomeForm, { IWelcomeFormValues } from './WelcomeForm/WelcomeForm'

type IFormState = 'init' | 'form' | 'success' | 'error'

function getWelcomeText(onboard?: IData) {
	if (onboard?.companyContractFlow === 'CONTRACT_BEFORE_REGISTRATION') {
		return 'loc:general|Welcome to WageNow. We will send you an electronic contract for signature to your email. After signing, your Wagenow account will be automatically created and we will send instructions for setting the password to the e-mail address you selected.'
	}
	return 'loc:general|Instructions for creating a password and a contract for signing have been sent to your chosen e-mail'
}

const WelcomePage: FC = () => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const { setLanguageFromSource } = useLanguageContext()
	const { isLoaded } = useLoadScript({
		googleMapsApiKey: window?.__RUNTIME_CONFIG__?.REACT_APP_PLACES_KEY
			? window.__RUNTIME_CONFIG__.REACT_APP_PLACES_KEY
			: process.env.REACT_APP_PLACES_KEY || '',
		libraries: ['places']
	})

	const [formState, setFormState] = useState<IFormState>('init')
	const { hash, s: expireHash } = qs.parse(document.location.search, { ignoreQueryPrefix: true })
	const { data: onboard } = useSelector((state: RootState) => state.onboarding)

	useEffect(() => {
		document.title = onboard?.onboardingForm?.title || `${t('loc:general|Welcome to Wagenow')}`
	}, [t, onboard?.onboardingForm?.title])

	useEffect(() => {
		if (hash) {
			dispatch(
				getOnboardingData(
					{
						hash: hash as string,
						expireHash: expireHash as string | undefined
					},
					() => setFormState('form'), // on success
					() => setFormState('error') // on error
				)
			)
		} else {
			setFormState('error')
		}
	}, [dispatch, hash, expireHash])

	useEffect(() => {
		if (onboard?.language) {
			setLanguageFromSource(onboard.language, 'onboarding')
		}
	}, [onboard?.language, setLanguageFromSource])

	/**
	 * Transforms individual banking fields into bankData discriminated union structure
	 * that matches the createCompanyEmployeeRequestSchema expected by the API
	 */
	const transformBankingFields = (values: IWelcomeFormValues) => {
		const cleanIban = values.iban?.replace(/\s/g, '')
		const cleanBankAccountNumber = values.bankAccountNumber?.replace(/\s/g, '')
		const cleanAccountNumber = values.accountNumber?.replace(/\s/g, '')
		const cleanSortCode = values.sortCode?.replace(/\s/g, '')
		const cleanBic = values.bic?.trim().toUpperCase()

		let bankData = null

		if (cleanIban) {
			bankData = {
				identifierType: 'iban' as const,
				iban: cleanIban
			}
		} else if (cleanBankAccountNumber) {
			bankData = {
				identifierType: 'iban' as const,
				iban: cleanBankAccountNumber
			}
		} else if (cleanAccountNumber) {
			bankData = {
				identifierType: 'uk' as const,
				accountNumber: cleanAccountNumber,
				sortCode: cleanSortCode,
				bic: cleanBic
			}
		}

		return bankData
	}

	const handleSubmit = (values: IWelcomeFormValues) => {
		const bankData = transformBankingFields(values)

		const submitValues = {
			email: values.email?.trim(),
			firstName: values.firstName?.trim() || null,
			lastName: values.lastName?.trim() || null,
			phoneNumber: values.phoneNumber ? `${values.phonePrefix || '+421'}${values.phoneNumber?.replace(/\s/g, '')}` : null,
			birthday: values.birthday,
			birthNumber: values.birthNumber?.trim() || null,
			employeeIdentifier: values.employeeIdentifier?.trim() || null,
			bankData,
			address: {
				city: values.city,
				zipCode: values.zipCode,
				street: `${values.street} ${values.houseNumber}`,
				country: values.country?.trim() || null,
				subdivision: values.subdivision?.trim() || null
			},
			businessName: values.businessName?.trim(),
			businessRegistrationNumber: values.businessRegistrationNumber
		}
		dispatch(
			postOnboardingData(
				submitValues,
				{
					hash: hash as string,
					expireHash: expireHash as string | undefined
				},
				() => {
					setFormState('success')
				}
			)
		)
	}

	const replacePlaceholders = (template: string | undefined, replacements: any) => {
		return template?.replace(/%%(\w+)%%/g, (match, key) => {
			return Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : ''
		})
	}

	const renderState = (state: IFormState) => {
		const textReplacements = {
			company_name: onboard?.name
		}

		const replacedParagraph = replacePlaceholders(onboard?.onboardingForm.paragraph, textReplacements)

		switch (state) {
			case 'init':
				return (
					<div className={'w-100vw h-100vh flex justify-center items-center'}>
						<Spin size={'large'} />
					</div>
				)
			case 'form':
			case 'success':
				return (
					<>
						<div
							className={'h-[250px] md:h-[370px] lg:h-[650px] w-[100vw] top-0 right-0 left-0 absolute bg-cover bg-center bg-no-repeat'}
							style={{ backgroundImage: `url('${onboard?.onboardingForm?.bgPicture}')` }}
						/>
						<div
							className={'pt-[180px] md:pt-[300px] lg:pt-[450px] justify-center flex items-center flex-col min-h-100vh'}
							style={{ backgroundColor: onboard?.onboardingForm?.bgColor }}
						>
							<div className={'justify-center flex items-center ml-auto mr-auto w-100%'}>
								<div
									className={
										'bg-white rounded-[16px] sm:rounded-[32px] lg:rounded-[80px] lg:p-[110px] p-[16px] sm:p-[32px] mb-[110px] w-[850px] z-10 ml-[16px] mr-[16px]'
									}
								>
									{state === 'success' && (
										<>
											<div className={`flex w-full justify-center`}>
												<img className={'relative h-[200px] w-[200px] md:h-[350px] md:w-[350px] mb-[48px]'} src={SuccessfullImage} alt={'Successful'} />
											</div>
											<h1 className={'text-28 font-700 mb-4'}>{onboard?.onboardingForm.title || t('loc:general|Welcome to Wagenow')}</h1>
											<p>{t(getWelcomeText(onboard))}</p>
										</>
									)}
									{state === 'form' && (
										<>
											<header className={'text-left'}>
												{/* <h1 className={'text-40 font-700 mb-4'}>{data?.name}</h1> */}
												<img
													className={'relative h-[40px] sm:h-[68px] w-auto sm:max-w-[500px] z-10 mb-1 sm:mb-3'}
													src={onboard?.onboardingForm.logo}
													alt={''}
												/>
												<h2 className={'text-36 font-700 mb-4'}>{onboard?.onboardingForm.title || t('loc:general|Welcome to Wagenow')}</h2>
												<p>{replacedParagraph}</p>
											</header>
											<WelcomeForm onSubmit={handleSubmit} />
										</>
									)}
								</div>
							</div>
						</div>
						{/* NOTE: only on form view, set body background to form bg color, beter looking smooth scrolling spring */}
						<style>{`body {background: ${onboard?.onboardingForm.bgColor}}`}</style>
					</>
				)
			case 'error':
				return (
					<div className={'w-100vw h-100vh flex justify-center items-center flex-col p-4'}>
						<h1 className={'text-40 font-700 mb-4'}>{'WageNow'}</h1>
						<p className={'text-center'}>{t('loc:general|The form with the specified address is not available')}</p>
						<p className={'text-center'}>
							{t('loc:general|If you received this address from your employer, please contact him directly or contact us at')}
							{' info@wagenow.sk'}
						</p>
					</div>
				)
			default:
				return (
					<div className={'w-100vw h-100vh flex justify-center items-center'}>
						<Spin size={'large'} />
						<p className={'text-center'}>{t('loc:Loading data')}...</p>
					</div>
				)
		}
	}

	if (!isLoaded) {
		return (
			<div className={'w-100vw h-100vh flex justify-center items-center'}>
				<Spin size={'large'} />
			</div>
		)
	}

	return renderState(formState)
}

export default WelcomePage
